import { useEffect, useRef } from "react";
import { start as startProcessingCue, stop as stopProcessingCue } from "../audio/processingCue";

// 処理中の合図音とマイク停止を扱う hook (spec 2026-07-27 §3.2)。
//
// 音とマイク停止を 1 箇所に閉じておけば、「SE を鳴らしたのにマイクが開いていた」状態が
// 構造的に作れなくなる (前回のターン合図音で earconPlayingRef が担っていたループバック・
// ガードを、そもそも発生させない形にする)。
//
// **鳴らす条件とマイクを止める条件は別々に受け取る (spec 2026-07-28 §3.7)。**
// 判定待ちのタイムアウトで読み上げが再開したとき、処理中の状態は続いたままなので、
// 1 つの条件で兼ねると AI の声の上に 850ms ごとの合図音が重なる。これは 2026-07-25 に
// 合図音を全廃した理由「音声が流れている時に鳴る SE で耳が痛い」そのもので、
// 再導入の条件に挙げた「音声と重ねない」に反する。
// 一方でマイクを止める判断 (処理中はユーザーの音声が要らない) は音とは独立しているので、
// そちらは処理中かどうかだけで決める。
//
// マイクの止め方を引数で受け取るのが要点: 本編と事前チェックでマイクの持ち主が違う
// (前者は useContinuousMode、後者は recognizeSingleUtterance の内部) ため、hook 自身は
// マイクの事情を知らない。
type Options = {
  /** 合図音を鳴らすか。読み上げが聞こえている間は false にする。 */
  cueActive: boolean;
  /** マイクを止めるか。処理中はユーザーの音声が要らないという独立した判断。 */
  micActive: boolean;
  suspendMic: () => void;
  resumeMic: () => void;
};

export function useProcessingCue({ cueActive, micActive, suspendMic, resumeMic }: Options): void {
  // 呼び出し側はインライン関数を渡すので、effect の依存に入れると identity が変わるたびに
  // マイクの停止と再開が往復してしまう。ref で最新版を保持し、依存は真偽値だけにする。
  const suspendRef = useRef(suspendMic);
  suspendRef.current = suspendMic;
  const resumeRef = useRef(resumeMic);
  resumeRef.current = resumeMic;

  // 効果の宣言順が後始末の順序を決める (React は宣言順に cleanup を走らせる)。
  // 後始末で守りたいのは「鳴りやんでからマイクを戻す」なので、音を先に宣言する。
  //
  // 立ち上げの順序は逆になる (音 → マイク) が、start() が行うのは 500ms 後の予約だけで、
  // 実際に音が出るのは同じ同期ブロックでマイクを止め終えた遥か後。したがって
  // 「鳴っている間にマイクが開いている」瞬間は生じない。
  useEffect(() => {
    if (!cueActive) return;
    startProcessingCue();
    return () => {
      stopProcessingCue();
    };
  }, [cueActive]);

  useEffect(() => {
    // 条件が立っていない間は cleanup を返さない。これがないと、一度も処理中に
    // ならないまま unmount しただけでマイクを開けてしまう。
    if (!micActive) return;
    suspendRef.current();
    return () => {
      resumeRef.current();
    };
  }, [micActive]);
}
